import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, TrashIcon, PlusIcon, CheckIcon, XMarkIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';
import { supabase } from '../../lib/supabase';
import type { UserRole } from '../../types/settings';

const userRoleSchema = z.object({
  name: z.string().min(1, 'Role name is required'),
  description: z.string().optional(),
  permissions: z.record(z.any()).optional()
});

type UserRoleFormData = z.infer<typeof userRoleSchema>;

// Define available permissions
const availablePermissions = {
  dashboard: {
    label: 'Dashboard',
    permissions: {
      view: 'View dashboard overview & stats'
    }
  },
  players: {
    label: 'Player Management',
    permissions: {
      read: 'View players',
      create: 'Register players',
      update: 'Edit player details',
      delete: 'Remove players',
      approve: 'Approve registrations'
    }
  },
  scores: {
    label: 'Scores & Scorecards',
    permissions: {
      read: 'View scores',
      create: 'Enter scores',
      update: 'Edit scores',
      delete: 'Delete scores'
    }
  },
  partners: {
    label: 'Partner Management',
    permissions: {
      read: 'View partners',
      create: 'Add partners',
      update: 'Edit partner details',
      delete: 'Remove partners',
      approve: 'Approve partnerships'
    }
  },
  events: {
    label: 'Event Calendar',
    permissions: {
      read: 'View events',
      create: 'Create events',
      update: 'Edit events',
      delete: 'Delete events'
    }
  },
  financials: {
    label: 'Financial Management',
    permissions: {
      read: 'View financial data',
      create: 'Add transactions',
      update: 'Edit transactions',
      delete: 'Delete transactions',
      approve: 'Approve transactions'
    }
  },
  content: {
    label: 'Content Management',
    permissions: {
      read: 'View content',
      create: 'Create content',
      update: 'Edit content',
      delete: 'Delete content'
    }
  },
  tournaments: {
    label: 'Tournament Management',
    permissions: {
      read: 'View tournaments',
      create: 'Create tournaments',
      update: 'Edit tournaments',
      delete: 'Delete tournaments'
    }
  },
  courses: {
    label: 'Course Management',
    permissions: {
      read: 'View courses',
      create: 'Add courses',
      update: 'Edit courses',
      delete: 'Delete courses'
    }
  },
  press: {
    label: 'Press & Media',
    permissions: {
      read: 'View press entries',
      create: 'Add press entries',
      update: 'Edit press entries',
      delete: 'Delete press entries'
    }
  },
  analytics: {
    label: 'Analytics',
    permissions: {
      view: 'View site analytics & traffic data'
    }
  },
  messaging: {
    label: 'Messaging',
    permissions: {
      read: 'View messaging',
      send: 'Send emails & push notifications'
    }
  },
  settings: {
    label: 'System Settings',
    permissions: {
      read: 'View settings',
      update: 'Update settings'
    }
  },
  users: {
    label: 'User Management',
    permissions: {
      read: 'View users',
      create: 'Create users',
      update: 'Edit users',
      delete: 'Delete users',
      assign_roles: 'Assign roles'
    }
  },
  account: {
    label: 'My Account',
    permissions: {
      manage: 'Access account settings and close account'
    }
  }
};

const BUILTIN_ROLES = [
  { key: 'admin', label: 'Admin', description: 'Full access to all features' },
  { key: 'super_admin', label: 'Super Admin', description: 'Full access to all features' },
  { key: 'manager', label: 'Manager', description: 'Manages players, scores, partners, finance' },
  { key: 'editor', label: 'Editor', description: 'Manages content, press, tournaments' },
  { key: 'scorer', label: 'Scorer', description: 'Manages scoring during events' },
  { key: 'technical', label: 'Technical', description: 'Calendar, scores, players management' },
  { key: 'viewer', label: 'Viewer', description: 'View-only dashboard access' },
];

export default function UserRolesForm() {
  const { state, updateUserRole, addUserRole, deleteUserRole } = useSettings();
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Record<string, Record<string, boolean>>>({});
  const [messagingPermissions, setMessagingPermissions] = useState<Record<string, boolean>>({
    admin: true,
    super_admin: true,
    manager: true,
    editor: true,
    scorer: true,
    technical: true,
    viewer: false,
  });
  const [savingMessaging, setSavingMessaging] = useState(false);

  useEffect(() => {
    loadMessagingPermissions();
  }, []);

  const loadMessagingPermissions = async () => {
    const { data } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'role_messaging_permissions')
      .maybeSingle();
    if (data?.value && typeof data.value === 'object') {
      setMessagingPermissions(prev => ({ ...prev, ...(data.value as Record<string, boolean>) }));
    }
  };

  const saveMessagingPermissions = async (updated: Record<string, boolean>) => {
    setSavingMessaging(true);
    await supabase
      .from('system_settings')
      .upsert({ key: 'role_messaging_permissions', value: updated }, { onConflict: 'key' });
    setSavingMessaging(false);
  };

  const toggleMessagingForRole = async (roleKey: string) => {
    if (roleKey === 'admin' || roleKey === 'super_admin') return;
    const updated = { ...messagingPermissions, [roleKey]: !messagingPermissions[roleKey] };
    setMessagingPermissions(updated);
    await saveMessagingPermissions(updated);
  };
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<UserRoleFormData>({
    resolver: zodResolver(userRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      permissions: {}
    }
  });

  const handleEdit = (role: UserRole) => {
    setEditingRole(role);
    reset({
      name: role.name,
      description: role.description || '',
    });
    
    // Initialize selected permissions from role permissions
    const permissionsObj: Record<string, Record<string, boolean>> = {};
    
    // Initialize all permissions as false
    Object.keys(availablePermissions).forEach(category => {
      permissionsObj[category] = {};
      Object.keys(availablePermissions[category as keyof typeof availablePermissions].permissions).forEach(perm => {
        permissionsObj[category][perm] = false;
      });
    });
    
    // Set permissions from role
    Object.entries(role.permissions).forEach(([category, perms]) => {
      if (typeof perms === 'object' && perms !== null) {
        Object.entries(perms).forEach(([perm, value]) => {
          if (permissionsObj[category]) {
            permissionsObj[category][perm] = Boolean(value);
          }
        });
      } else if (perms === true) {
        // Handle case where all permissions in a category are granted
        if (permissionsObj[category]) {
          Object.keys(permissionsObj[category]).forEach(perm => {
            permissionsObj[category][perm] = true;
          });
        }
      }
    });
    
    setSelectedPermissions(permissionsObj);
    setIsAdding(false);
  };

  const handleAdd = () => {
    setEditingRole(null);
    reset({
      name: '',
      description: '',
      permissions: {}
    });
    
    // Initialize all permissions as false
    const permissionsObj: Record<string, Record<string, boolean>> = {};
    Object.keys(availablePermissions).forEach(category => {
      permissionsObj[category] = {};
      Object.keys(availablePermissions[category as keyof typeof availablePermissions].permissions).forEach(perm => {
        permissionsObj[category][perm] = false;
      });
    });
    
    setSelectedPermissions(permissionsObj);
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user role?')) {
      try {
        await deleteUserRole(id);
      } catch (error) {
        console.error('Error deleting user role:', error);
      }
    }
  };

  const handlePermissionChange = (category: string, permission: string, checked: boolean) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [permission]: checked
      }
    }));
  };

  const handleCategoryPermissionChange = (category: string, checked: boolean) => {
    setSelectedPermissions(prev => {
      const updatedCategory: Record<string, boolean> = {};
      Object.keys(availablePermissions[category as keyof typeof availablePermissions].permissions).forEach(perm => {
        updatedCategory[perm] = checked;
      });
      
      return {
        ...prev,
        [category]: updatedCategory
      };
    });
  };

  const isCategoryFullySelected = (category: string) => {
    const categoryPermissions = selectedPermissions[category];
    if (!categoryPermissions) return false;
    
    return Object.values(categoryPermissions).every(Boolean);
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryPermissions = selectedPermissions[category];
    if (!categoryPermissions) return false;
    
    const values = Object.values(categoryPermissions);
    return values.some(Boolean) && !values.every(Boolean);
  };

  const onSubmit = async (data: UserRoleFormData) => {
    try {
      // Convert selected permissions to the format expected by the API
      const formattedPermissions: Record<string, any> = {};
      
      Object.entries(selectedPermissions).forEach(([category, permissions]) => {
        formattedPermissions[category] = {};
        Object.entries(permissions).forEach(([permission, isSelected]) => {
          formattedPermissions[category][permission] = isSelected;
        });
        
        // If all permissions in a category are selected, simplify to true
        if (Object.values(permissions).every(Boolean)) {
          formattedPermissions[category] = true;
        }
      });
      
      if (editingRole) {
        await updateUserRole({
          ...editingRole,
          name: data.name,
          description: data.description,
          permissions: formattedPermissions
        });
      } else {
        await addUserRole({
          name: data.name,
          description: data.description,
          permissions: formattedPermissions
        });
      }
      
      setEditingRole(null);
      setIsAdding(false);
      reset();
    } catch (error) {
      console.error('Error saving user role:', error);
    }
  };

  const cancelEdit = () => {
    setEditingRole(null);
    setIsAdding(false);
    reset();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">User Roles & Permissions</h2>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Role</span>
        </button>
      </div>

      {/* Messaging Permissions by Role */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <EnvelopeIcon className="w-5 h-5 text-golf-orange" />
          <h3 className="text-lg font-medium text-golf-gray">Messaging Access by Role</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Control which roles can access the Messaging features (email and push notifications).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {BUILTIN_ROLES.map(role => {
            const isEnabled = messagingPermissions[role.key] ?? false;
            const isLocked = role.key === 'admin' || role.key === 'super_admin';
            return (
              <div
                key={role.key}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors ${
                  isEnabled ? 'border-golf-orange/30 bg-golf-orange/5' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 capitalize">{role.label}</p>
                  <p className="text-xs text-gray-500 truncate">{role.description}</p>
                </div>
                <label className="relative inline-flex items-center ml-3 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => toggleMessagingForRole(role.key)}
                    disabled={isLocked || savingMessaging}
                    className="sr-only peer"
                  />
                  <div className={`w-9 h-5 rounded-full peer-focus:ring-2 peer-focus:ring-golf-orange/30 transition-colors ${
                    isEnabled ? 'bg-golf-orange' : 'bg-gray-300'
                  } ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      isEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </div>
                </label>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-3">Admin and Super Admin roles always have messaging access.</p>
      </div>

      {(isAdding || editingRole) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-golf-gray mb-4">
            {editingRole ? 'Edit User Role' : 'Add New User Role'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  {...register('description')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Permissions
              </label>
              <div className="space-y-6">
                {Object.entries(availablePermissions).map(([category, { label, permissions }]) => (
                  <div key={category} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id={`category-${category}`}
                        checked={isCategoryFullySelected(category)}
                        onChange={(e) => handleCategoryPermissionChange(category, e.target.checked)}
                        className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                      />
                      <label htmlFor={`category-${category}`} className="ml-2 block text-sm font-medium text-gray-700">
                        {label}
                        {isCategoryPartiallySelected(category) && (
                          <span className="ml-2 text-xs text-gray-500">(Some permissions selected)</span>
                        )}
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                      {Object.entries(permissions).map(([permission, description]) => (
                        <div key={`${category}-${permission}`} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`${category}-${permission}`}
                            checked={selectedPermissions[category]?.[permission] || false}
                            onChange={(e) => handlePermissionChange(category, permission, e.target.checked)}
                            className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                          />
                          <label htmlFor={`${category}-${permission}`} className="ml-2 block text-sm text-gray-600">
                            {description}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
              >
                {editingRole ? 'Update Role' : 'Add Role'}
              </button>
            </div>
          </form>
        </div>
      )}

      {state.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : state.error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {state.error}
        </div>
      ) : (
        <div className="space-y-6">
          {state.userRoles.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Permissions
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {state.userRoles.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{role.name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-500">{role.description || '-'}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(role.permissions).map(([category, perms]) => {
                              // If perms is true, it means all permissions in that category
                              if (perms === true) {
                                return (
                                  <span key={category} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {availablePermissions[category as keyof typeof availablePermissions]?.label || category}
                                  </span>
                                );
                              }
                              
                              // If perms is an object, check which permissions are granted
                              if (typeof perms === 'object' && perms !== null) {
                                const grantedPerms = Object.entries(perms)
                                  .filter(([_, value]) => value === true)
                                  .map(([perm, _]) => perm);
                                
                                if (grantedPerms.length > 0) {
                                  return (
                                    <span key={category} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {availablePermissions[category as keyof typeof availablePermissions]?.label || category}
                                    </span>
                                  );
                                }
                              }
                              
                              return null;
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(role)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(role.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              disabled={role.name === 'admin'}
                              title={role.name === 'admin' ? 'Cannot delete admin role' : 'Delete role'}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <p className="text-gray-500">No user roles found. Add your first role using the button above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}